import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import _ from 'lodash';
import NumberFormat from 'react-number-format';
import {
  Formik, Form, Field, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';
import {t} from '../../../services/i18nHandler';
import Modal from '../../Modal/ModalNew';
import {CloseIcon} from '../../SvgComponents/SvgComponents';
import './ModalIndividualTariff.less';
import {onlyNumbers} from '../../../services/common';
import FilePickerInline from '../../UI/FilePickerInline/FilePickerInline';
import TariffsApi from '../../../api/tariffs.api';
import {NotificationError, NotificationSuccess} from '../../../services/notifications';

const initialValues = {
  tariff: '',
  hoursOfRecord: '',
  mbOfRecords: '',
  daysOfAccess: 30,
  activationsLimit: 1,
  text: '',
  fullName: '',
  contactEmail: '',
  phoneNumber: '',
};

const validationSchema = Yup.object().shape({
  tariff: Yup.string().required('Поле обязательно к заполнению'),
  text: Yup.string().required('Поле обязательно к заполнению'),
  fullName: Yup.string().required('Поле обязательно к заполнению'),
  phoneNumber: Yup.string().required('Поле обязательно к заполнению'),
  activationsLimit: Yup.number(),
  contactEmail: Yup.string()
    .email('Неправильный формат email')
    .max(60, 'Максимальная длина email 60 символов')
    .required('Поле обязательно к заполнению'),
});

const MB_PER_HOUR = 850;

function ModalIndividualTariff({
  onClose, isOpen, tariffs, language, serviceOptions,
}) {
  const formRef = useRef();

  const [attachedFile, changeAttachedFile] = useState(null);

  const [loading, setLoading] = useState(false);

  const [selectedTariffId, setSelectedTariffId] = useState(null);

  const selectedTariff = useMemo(() => (_.find(tariffs, {_id: selectedTariffId}) || {}), [tariffs, selectedTariffId]);

  const selectTariffOptions = useMemo(() => (tariffs
    .filter((tariff) => {
      const tariffName = tariff.name || {};
      return tariffName.ru !== 'Бесплатно';
    })
    .map((tariff) => ({
      value: tariff._id,
      label: tariff.name[language],
    }))), [tariffs, language]);

  const attributes = useMemo(() => {
    const attrs = selectedTariff.attributes || [];
    return attrs.filter((a) => ((a !== 'UNLIMITED_CREATE_VIDEOPOLL' && a !== 'UNLIMITED_RECORD_VIDEOSESSION')));
  }, [selectedTariff]);

  const attributeDescription = useMemo(() => (attr) => {
    const currentAttr = _.find(serviceOptions, {value: attr}) || {};
    const attrText = currentAttr.text || {};
    return attrText[language] || attr;
  }, [serviceOptions, language]);

  const selectedTariffOption = useMemo(() => (selectedTariff.standardOptions || {}), [selectedTariff]);

  const totalHours = useMemo(() => {
    const hours = selectedTariffOption.totalHoursRecord;
    if (!hours) {
      return null;
    }
    const isUnlimited = selectedTariffOption.unlimitedRecord;
    if (isUnlimited) {
      return (
        <div className="tariffs_details_services_hours_infinite" />
      );
    }
    return (<div className="tariffs_details_services_hours_value">{hours}</div>);
  }, [selectedTariff, selectedTariffOption]);

  const onSendIndividualTariffRequest = useCallback(async (data) => {
    setLoading(true);
    await TariffsApi.requestIndividualTariff(data).then(() => {
      onClose();
      NotificationSuccess('Форма успешно отправлена');
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
      setLoading(false);
    });
  }, [onClose]);

  const onSubmit = useCallback((values) => {
    if (attachedFile) {
      const formData = new FormData();
      for (const p in values) {
        formData.append(p, values[p]);
      }
      formData.append('attachment', attachedFile);
      onSendIndividualTariffRequest(formData);
    } else {
      onSendIndividualTariffRequest(values);
    }
  }, [attachedFile]);

  const onTariffChanged = useCallback(() => {
    if (!formRef.current) {
      return null;
    }
    const hours = selectedTariffOption.totalHoursRecord || 0;
    const isUnlimited = selectedTariffOption.unlimitedRecord;
    formRef.current.setFieldValue('hoursOfRecord', isUnlimited ? 0 : hours);
    formRef.current.setFieldValue('mbOfRecords', +hours * MB_PER_HOUR);
  }, [selectedTariffOption]);

  useEffect(() => {
    onTariffChanged();
  }, [selectedTariff]);

  return (
    <Modal onHide={onClose} isOpen={isOpen} className="modal-center">
      <div className="modal-inner">
        <div className="modal-container" style={{width: '1200px'}}>
          <div className="modal-header">
            <div className="modal-header_title">{t('modalIndividualTariff.title')}</div>
            <button type="button" className="modal-close-btn" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="modal-content">
            <div className="modal-text">
              {t('modalIndividualTariff.text')}
            </div>
            <div className="modal-individual-tariff">
              <Formik
                ref={formRef}
                onSubmit={onSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
              >
                <Form>
                  <div className="row">
                    <div className="col-lg-8">
                      <div className="create-form_content_section">
                        <div className="create-form_field">
                          <div className="create-form_field_label">
                            {t('modalIndividualTariff.tariffLabel')}
                          </div>
                          <div className="row">
                            <div className="col-lg-8">
                              <Field
                                name="tariff"
                                render={({form}) => (
                                  <Select
                                    options={selectTariffOptions}
                                    classNamePrefix="common-select"
                                    placeholder={t('modalIndividualTariff.selectTariff')}
                                    isSearchable={false}
                                    onChange={(data) => {
                                      setSelectedTariffId(data.value);
                                      form.setFieldValue('tariff', data.label);
                                    }}
                                  />
                                )}
                              />
                              <ErrorMessage
                                className="error-text"
                                name="tariff"
                                component="div"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="create-form_field">
                          <div className="create-form_field_horizontal-section">
                            <Field
                              name="hoursOfRecord"
                              render={({form, field}) => (
                                <input
                                  className="ui-input ui-input-short"
                                  value={field.value}
                                  onChange={(event) => {
                                    const _value = event.target.value;
                                    const isNumber = onlyNumbers.test(_value);
                                    if (!isNumber && _value.length) {
                                      return;
                                    }
                                    const mbValue = _value ? +(_value * MB_PER_HOUR) : '';
                                    const hoursValue = _value ? +_value : '';
                                    form.setFieldValue('mbOfRecords', mbValue);
                                    form.setFieldValue('hoursOfRecord', hoursValue);
                                  }}
                                />
                              )}
                            />
                            <div className="create-form_field_text right left no-shrink">
                              {t('modalIndividualTariff.recordingHours')}
                            </div>
                            <div className="create-form_field_label">
                              {t('modalIndividualTariff.or')}
                            </div>
                            <Field
                              name="mbOfRecords"
                              render={({form, field}) => (
                                <input
                                  className="ui-input ui-input-short"
                                  value={field.value}
                                  onChange={(event) => {
                                    const _value = event.target.value;
                                    const isNumber = onlyNumbers.test(_value);
                                    if (!isNumber && _value.length) {
                                      return;
                                    }
                                    const hoursValue = _value ? +(_value / MB_PER_HOUR).toFixed(2) : '';
                                    const mbValue = _value ? +_value : '';
                                    form.setFieldValue('hoursOfRecord', hoursValue);
                                    form.setFieldValue('mbOfRecords', mbValue);
                                  }}
                                />
                              )}
                            />
                            <div className="create-form_field_text right left no-shrink">
                              {t('modalIndividualTariff.mB')}
                            </div>
                          </div>
                        </div>
                        <div className="create-form_field">
                          <div className="row">
                            <div className="col-lg-6">
                              <div className="create-form_field_label">
                                {t('modalIndividualTariff.periodLabel')}
                              </div>
                              <div className="create-form_field_horizontal-section">
                                <div className="create-form_field_horizontal-section_item">
                                  <Field
                                    type="number"
                                    name="daysOfAccess"
                                    className="ui-input ui-input-short"
                                  />
                                  <ErrorMessage
                                    className="error-text"
                                    name="daysOfAccess"
                                    component="div"
                                  />
                                </div>
                                <div className="create-form_field_text right no-shrink">
                                  {t('modalIndividualTariff.days')}
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="create-form_field_label">
                                {t('modalIndividualTariff.countLabel')}
                              </div>
                              <Field
                                type="number"
                                name="activationsLimit"
                                className="ui-input ui-input-short"
                              />
                              <ErrorMessage
                                className="error-text"
                                name="activationsLimit"
                                component="div"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="create-form_field">
                          <div className="row">
                            <div className="col-lg-4">
                              <div className="create-form_field_label">
                                {t('modalIndividualTariff.nameLabel')}
                              </div>
                              <Field
                                type="text"
                                className="ui-input"
                                name="fullName"
                                placeholder={t('modalIndividualTariff.namePlaceholder')}
                              />
                              <ErrorMessage
                                className="error-text"
                                name="fullName"
                                component="div"
                              />
                            </div>
                            <div className="col-lg-4">
                              <div className="create-form_field_label">
                                {t('modalIndividualTariff.phoneLabel')}
                              </div>
                              <Field
                                name="phoneNumber"
                                render={({field, form}) => (
                                  <NumberFormat
                                    value={field.value}
                                    format="+# (###) ###-##-########"
                                    mask=""
                                    placeholder="+7 (XXX) XXX-XX-XX"
                                    className="ui-input"
                                    onChange={(e) => {
                                      form.setFieldValue('phoneNumber', e.target.value);
                                    }}
                                  />
                                )}
                              />
                              <ErrorMessage
                                className="error-text"
                                name="phoneNumber"
                                component="div"
                              />
                            </div>
                            <div className="col-lg-4">
                              <div className="create-form_field_label">
                                {t('modalIndividualTariff.emailLabel')}
                              </div>
                              <Field
                                type="text"
                                className="ui-input"
                                name="contactEmail"
                                placeholder={t('modalIndividualTariff.emailPlaceholder')}
                              />
                              <ErrorMessage
                                className="error-text"
                                name="contactEmail"
                                component="div"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="create-form_field">
                          <div className="create-form_field_label">
                            {t('modalIndividualTariff.descriptionLabel')}
                          </div>
                          <Field
                            type="text"
                            component="textarea"
                            name="text"
                            className="ui-textarea"
                          />
                          <ErrorMessage
                            className="error-text"
                            name="text"
                            component="div"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="tariffs_details_services-list">
                        <div className="create-form_field_label">
                          {t('modalIndividualTariff.optionsLabel')}
                        </div>
                        {attributes.map((a) => (
                          <div className="tariffs_details_services" key={a}>
                            <div className="tariffs_details_services_icon">
                              <i className="icon-check" />
                            </div>
                            <div className="tariffs_details_services_text">
                              {attributeDescription(a)}
                            </div>
                          </div>
                        ))}
                        {totalHours && (
                          <div className="tariffs_details_services">
                            <div className="tariffs_details_services_hours">
                              {totalHours}
                              <div className="tariffs_details_services_hours_unit">
                                {t('units.hours')}
                              </div>
                            </div>
                            <div className="tariffs_details_services_text">
                              {t('tariffs.totalHours')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="create-form_field">
                        <div className="row align-centr">
                          <div className="col-lg-4">
                            <FilePickerInline
                              onChange={changeAttachedFile}
                              placeholder={t('modalIndividualTariff.requisites')}
                              attachedFile={attachedFile}
                              allowedExtensions={['pdf', 'doc', 'docx', 'txt', 'png', 'jpg', 'xls', 'xlsx']}
                            />
                          </div>
                          <div className="col-lg-4 text-center">
                            <div className="btn-wrap">
                              <button type="submit" className="btn btn-base">
                                {loading ? t('buttons.dispatch') : t('buttons.submit')}
                                {loading && (<span className="loading-circle" />)}
                              </button>
                              <button type="button" className="btn btn-conture" onClick={onClose}>
                                {t('buttons.cancel')}
                              </button>
                            </div>
                          </div>
                          <div className="col-lg-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

ModalIndividualTariff.defaultProps = {
  isOpen: false,
  tariffs: [],
  serviceOptions: [],
  language: 'ru',
};

ModalIndividualTariff.propTypes = {
  tariffs: PropTypes.arrayOf(PropTypes.shape({})),
  serviceOptions: PropTypes.arrayOf(PropTypes.shape({})),
  onClose: PropTypes.func.isRequired,
  language: PropTypes.string,
  isOpen: PropTypes.bool,
};

export default ModalIndividualTariff;
